/**
 * AtcManifestFetchService - Network fetching layer for ATC manifests
 *
 * Single Responsibility: Performs HTTP requests to the CDN, validates
 * responses with Zod schemas, and wraps calls in circuit-breaker
 * protection.  Has no knowledge of caching or SWR.
 */

import { Logger } from '@/app/utils/logger';
import { DataFetchLogger } from '@/app/services/logging/DataFetchLogger';
import {
  AtcMainManifest,
  AtcStationManifest,
} from '@/types/AtcManifest';
import { AtcManifestResult } from '@/types/AtcCache';
import {
  safeParseMainManifest,
  safeParseStationManifest,
} from './schemas/AtcManifestSchemas';
import {
  AtcManifestValidationError,
  createMainManifestValidationError,
  createStationManifestValidationError,
} from './errors/AtcManifestValidationError';
import {
  getCircuitBreaker,
  CircuitBreakerService,
} from '@/app/services/resilience';

const LOG_TAG = 'atc-manifest-fetch';

/**
 * Get the circuit breaker for ATC manifest requests
 */
export function getManifestCircuitBreaker(): CircuitBreakerService {
  return getCircuitBreaker('atc-manifest');
}

/**
 * Get the circuit breaker for ATC station requests
 */
export function getStationCircuitBreaker(): CircuitBreakerService {
  return getCircuitBreaker('atc-station');
}

/**
 * Get the base CDN URL from environment
 */
export function getCdnBaseUrl(): string {
  const cdnUrl = import.meta.env.VITE_ATC_CDN_URL || 'https://cdn.atc.chillairwaves.com';
  return cdnUrl.endsWith('/') ? cdnUrl.slice(0, -1) : cdnUrl;
}

/**
 * Fetch + validate result type (always "network" source, fresh).
 */
export interface NetworkFetchResult<T> extends AtcManifestResult<T> {
  source: 'network';
  etag?: string;
}

/**
 * Fetch the main manifest from the CDN.
 *
 * Validates data with Zod schemas; protected by circuit breaker.
 */
export async function fetchMainManifestFromNetwork(): Promise<NetworkFetchResult<AtcMainManifest>> {
  const url = `${getCdnBaseUrl()}/manifest.json`;
  const startTime = performance.now();
  Logger.info(`Fetching main manifest from ${url}`, LOG_TAG);

  const circuitBreaker = getManifestCircuitBreaker();

  try {
    const response = await circuitBreaker.execute(async () => {
      const resp = await fetch(url, {
        headers: { 'Accept': 'application/json' },
      });
      if (!resp.ok) {
        throw new Error(`Failed to fetch main manifest: ${resp.status} ${resp.statusText}`);
      }
      return resp;
    });

    const rawData: unknown = await response.json();
    const durationMs = Math.round(performance.now() - startTime);
    const etag = response.headers.get('etag') || undefined;

    // Validate
    const parseResult = safeParseMainManifest(rawData);
    if (!parseResult.success) {
      const validationError = createMainManifestValidationError(
        parseResult.error,
        url,
        rawData,
      );
      Logger.error(
        `Main manifest validation failed: ${JSON.stringify(validationError.toLogObject())}`,
        LOG_TAG,
      );
      throw validationError;
    }

    const data = parseResult.data as unknown as AtcMainManifest;

    DataFetchLogger.logFetch({
      operation: 'manifest:fetch',
      source: 'atc-cdn',
      status: 'success',
      durationMs,
      etag,
      itemCount: data.station_count,
    });

    Logger.info(`Loaded main manifest with ${data.station_count} stations`, LOG_TAG);

    return {
      data,
      source: 'network',
      isStale: false,
      cacheAge: 0,
      etag,
    };
  } catch (error) {
    const durationMs = Math.round(performance.now() - startTime);
    const message = error instanceof Error ? error.message : String(error);

    if (AtcManifestValidationError.isAtcManifestValidationError(error)) {
      DataFetchLogger.logFetch({
        operation: 'manifest:fetch',
        source: 'atc-cdn',
        status: 'failure',
        durationMs,
        errorMessage: `Validation error: ${error.getFormattedIssues().join(', ')}`,
      });
    } else {
      DataFetchLogger.logFetch({
        operation: 'manifest:fetch',
        source: 'atc-cdn',
        status: 'failure',
        durationMs,
        errorMessage: message,
      });
    }

    Logger.error(`Error fetching main manifest: ${message}`, LOG_TAG);
    throw error;
  }
}

/**
 * Fetch a station manifest from the CDN.
 *
 * Validates data with Zod schemas; protected by circuit breaker.
 */
export async function fetchStationManifestFromNetwork(
  manifestUrl: string,
): Promise<NetworkFetchResult<AtcStationManifest>> {
  const startTime = performance.now();
  Logger.info(`Fetching station manifest from ${manifestUrl}`, LOG_TAG);

  const circuitBreaker = getStationCircuitBreaker();

  try {
    const response = await circuitBreaker.execute(async () => {
      const resp = await fetch(manifestUrl, {
        headers: { 'Accept': 'application/json' },
      });
      if (!resp.ok) {
        throw new Error(`Failed to fetch station manifest: ${resp.status} ${resp.statusText}`);
      }
      return resp;
    });

    const rawData: unknown = await response.json();
    const durationMs = Math.round(performance.now() - startTime);
    const etag = response.headers.get('etag') || undefined;

    // Validate
    const parseResult = safeParseStationManifest(rawData);
    if (!parseResult.success) {
      const validationError = createStationManifestValidationError(
        parseResult.error,
        manifestUrl,
        rawData,
      );
      Logger.error(
        `Station manifest validation failed: ${JSON.stringify(validationError.toLogObject())}`,
        LOG_TAG,
      );
      throw validationError;
    }

    const data = parseResult.data as unknown as AtcStationManifest;

    DataFetchLogger.logFetch({
      operation: 'stations:fetch',
      source: 'atc-cdn',
      status: 'success',
      durationMs,
      etag,
      itemCount: data.scenario_count,
    });

    Logger.info(`Loaded station manifest with ${data.scenario_count} scenarios`, LOG_TAG);

    return {
      data,
      source: 'network',
      isStale: false,
      cacheAge: 0,
      etag,
    };
  } catch (error) {
    const durationMs = Math.round(performance.now() - startTime);
    const message = error instanceof Error ? error.message : String(error);

    if (AtcManifestValidationError.isAtcManifestValidationError(error)) {
      DataFetchLogger.logFetch({
        operation: 'stations:fetch',
        source: 'atc-cdn',
        status: 'failure',
        durationMs,
        errorMessage: `Validation error: ${error.getFormattedIssues().join(', ')}`,
      });
    } else {
      DataFetchLogger.logFetch({
        operation: 'stations:fetch',
        source: 'atc-cdn',
        status: 'failure',
        durationMs,
        errorMessage: message,
      });
    }

    Logger.error(`Error fetching station manifest: ${message}`, LOG_TAG);
    throw error;
  }
}
